import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { v4 as uuidv4 } from 'uuid';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

/**
 * Creates a storage bucket if it doesn't already exist
 * @param bucketName The name of the bucket to create
 * @param isPublic Whether the bucket should be public (default: true)
 * @returns A boolean indicating success or failure
 */
export const createBucketIfNotExists = async (
  bucketName: string = 'paymentproofs',
  isPublic: boolean = true
): Promise<boolean> => {
  try {
    console.log(`Checking if bucket '${bucketName}' exists...`);
    
    // Check if bucket exists
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error('Error listing buckets:', listError);
      // Continue with upload even if we can't check for bucket existence
      return true;
    }
    
    const bucketExists = buckets?.some(bucket => bucket.name === bucketName);
    console.log(`Bucket '${bucketName}' exists: ${bucketExists}`);

    if (!bucketExists) {
      try {
        console.log(`Creating bucket '${bucketName}'...`);
        // Try to create the bucket, but don't throw if it fails
        const { error } = await supabase.storage.createBucket(bucketName, {
          public: isPublic,
          fileSizeLimit: 10485760, // 10MB
          allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
        });

        if (error) {
          console.warn(`Could not create bucket '${bucketName}':`, error.message);
          
          // If the error is due to RLS policy, try to update the policy
          if (error.message.includes('row-level security') || error.message.includes('Unauthorized')) {
            console.log('Attempting to bypass RLS by using admin API...');
            // We'll continue anyway and let the upload attempt proceed
          }
        } else {
          console.log(`Bucket '${bucketName}' created successfully`);
          
          // Set bucket policy to public if needed
          if (isPublic) {
            try {
              console.log(`Setting public access policy for bucket '${bucketName}'...`);
              const { data: policyData, error: policyError } = await supabase.storage.from(bucketName).getPublicUrl('dummy.txt');
              
              if (policyError && !policyError.message.includes('not found')) {
                console.warn('Error setting bucket policy:', policyError);
              } else {
                console.log(`Public access policy set for bucket '${bucketName}'`);
              }
            } catch (policyError) {
              console.warn('Error setting bucket policy:', policyError);
            }
          }
        }
      } catch (createError) {
        console.warn('Error creating bucket, will try to use existing:', createError);
        // Continue anyway - the bucket might already exist or be created by another process
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking bucket existence:', error);
    // Return true to allow the upload to proceed anyway
    return true;
  }
};

/**
 * Uploads a file to Supabase storage
 * @param file The file to upload
 * @param bucket The storage bucket name
 * @param folder The folder path within the bucket
 * @returns The URL of the uploaded file
 */
export const uploadFile = async (
  file: File,
  bucket: string = 'paymentproofs',
  folder: string = 'players',
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    // First ensure the bucket exists
    await createBucketIfNotExists(bucket, true);
    
    // Resize the image if it's an image file
    let fileToUpload = file;
    if (file.type.startsWith('image/')) {
      fileToUpload = await resizeImage(file);
    }
    
    // Generate a unique file name to prevent collisions
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;

    // Upload the file to Supabase storage
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, fileToUpload, {
        cacheControl: '3600',
        upsert: false,
        onUploadProgress: (progress) => {
          if (onProgress) {
            const percent = Math.round((progress.loaded / progress.total) * 100);
            onProgress(percent);
          }
        }
      });

    if (error) throw error;

    // Get the public URL for the file
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Deletes a file from Supabase storage
 * @param url The URL of the file to delete
 * @returns A boolean indicating success or failure
 */
const deleteFile = async (url: string): Promise<boolean> => {
  try {
    // Extract the path from the URL
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const bucket = pathParts[pathParts.length - 3];
    const filePath = pathParts.slice(pathParts.length - 2).join('/');

    // Delete the file
    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    return false;
  }
};

/**
 * Gets a signed URL for a file in Supabase storage
 * @param path The path to the file within the bucket
 * @param bucketName The storage bucket name
 * @param expiresIn The number of seconds until the URL expires
 * @returns The signed URL or null if an error occurs
 */
export const getSignedUrl = async (
  path: string,
  bucketName: string = 'paymentproofs',
  expiresIn: number = 3600
): Promise<string | null> => {
  try {
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(path, expiresIn);

    if (error) {
      console.error('Error generating signed URL:', error);
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Error generating signed URL:', error);
    return null;
  }
};

/**
 * Uploads multiple files to Supabase storage
 * @param files The files to upload
 * @param bucket The storage bucket name
 * @param folder The folder path within the bucket
 * @param onProgress Optional callback for upload progress
 * @returns An array of URLs for the uploaded files
 */
export const uploadMultipleFiles = async (
  files: File[],
  bucket: string = 'images',
  folder: string = 'gallery',
  onProgress?: (progress: number, index: number) => void
): Promise<string[]> => {
  try {
    // First ensure the bucket exists
    await createBucketIfNotExists(bucket, true);
    
    const urls: string[] = [];
    
    // Upload each file sequentially
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Resize the image before uploading
      const resizedFile = await resizeImage(file);
      
      // Generate a unique file name to prevent collisions
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;
      
      // Upload the file with progress tracking
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, resizedFile, {
          cacheControl: '3600',
          upsert: false,
          onUploadProgress: (progress) => {
            if (onProgress) {
              const percent = Math.round((progress.loaded / progress.total) * 100);
              onProgress(percent, i);
            }
          }
        });
      
      if (error) throw error;
      
      // Get the public URL for the file
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);
      
      urls.push(urlData.publicUrl);
    }
    
    return urls;
  } catch (error) {
    console.error('Error uploading files:', error);
    throw error;
  }
};

/**
 * Resizes an image to ensure the shortest side is 800px
 * @param file The image file to resize
 * @returns A Promise that resolves to the resized file
 */
export const resizeImage = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Check if the file is an image
    if (!file) {
      console.warn('No file provided to resizeImage');
      resolve(file); // Return original file (which is null/undefined)
      return;
    }
    
    if (!file.type || !file.type.startsWith('image/')) {
      resolve(file); // Not an image, return the original file
      return;
    }

    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Could not get canvas context');
      resolve(file); // Return original file if canvas context is not available
      return;
    }

    img.onload = () => {
      // Determine which dimension is shorter
      const imgWidth = img.width || 0;
      const imgHeight = img.height || 0;
      
      if (imgWidth === 0 || imgHeight === 0) {
        console.warn('Image has invalid dimensions', { width: imgWidth, height: imgHeight });
        resolve(file);
        return;
      }
      
      const isWidthShorter = imgWidth < imgHeight;
      
      // Calculate new dimensions ensuring shortest side is 800px
      let newWidth, newHeight;
      
      if (isWidthShorter) {
        // Width is shorter, set it to 800px and scale height proportionally
        if (imgWidth <= 800) {
          // Image is already small enough, no need to resize
          resolve(file);
          return;
        }
        
        newWidth = 800;
        newHeight = Math.round((imgHeight / imgWidth) * 800);
      } else {
        // Height is shorter, set it to 800px and scale width proportionally
        if (imgHeight <= 800) {
          // Image is already small enough, no need to resize
          resolve(file);
          return;
        }
        
        newHeight = 800;
        newWidth = Math.round((imgWidth / imgHeight) * 800);
      }
      
      // Set canvas dimensions to the new size
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw the resized image on the canvas
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      
      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Failed to create blob from canvas');
            resolve(file); // Return original file if blob creation fails
            return;
          }
          
          // Create a new File object from the blob
          const resizedFile = new File([blob], file?.name || 'image.jpg', {
            type: file.type,
            lastModified: Date.now()
          });
          
          resolve(resizedFile);
        },
        file.type,
        0.9 // Quality parameter (0.9 = 90% quality)
      );
    };
    
    img.onerror = () => {
      console.error('Error loading image for resizing', file?.name || 'unknown file');
      resolve(file); // Return original file if there's an error loading the image
    };
    
    // Load the image from the file
    try {
      img.src = URL.createObjectURL(file);
    } catch (error) {
      console.error('Error creating object URL for file', error);
      resolve(file);
    }
  });
};